import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  MessageCircle, Search, Filter, Settings, Bot, BotOff, Send, Sparkles,
  Clock, CheckCircle, AlertCircle, ChevronDown, User, Mail, MoreVertical,
  Archive, Trash2, RefreshCw, Zap, ArrowRight, X, Moon, Sun,
  MessageSquare, Users, TrendingUp, Inbox, ChevronRight, ExternalLink,
  Check, XCircle, Loader2
} from 'lucide-react';
import PageHeader from '../components/admin/PageHeader';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

const STATUS_CONFIG = {
  open: { label: 'Open', cls: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  pending: { label: 'Pending', cls: 'bg-blue-100 text-blue-700', icon: Clock },
  resolved: { label: 'Resolved', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  closed: { label: 'Closed', cls: 'bg-gray-100 text-gray-600', icon: Archive },
};

export default function Support() {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [counts, setCounts] = useState({ open: 0, pending: 0, resolved: 0, total: 0 });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [aiHealth, setAiHealth] = useState(null);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [aiSettings, setAiSettings] = useState({ enabled: true, offHoursOnly: false, startHour: 18, endHour: 9, timezone: 'America/New_York' });
  const [savingSettings, setSavingSettings] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const quickReplies = [
    { label: 'Greeting', text: 'Hi there! Thanks for reaching out to Squadline support. How can I help you today?' },
    { label: 'Looking into it', text: "Great question! Let me look into this for you. I'll have an answer shortly." },
    { label: 'Password reset', text: 'To reset your password, tap "Forgot Password" on the login screen and enter your email. You\'ll receive a reset link within a few minutes.' },
    { label: 'Bug reported', text: "Thanks for reporting this! I've logged it with our development team and we'll get it fixed. Is there anything else I can help with?" },
    { label: 'Feature request', text: "That's a great suggestion! I've passed it along to our product team. We're always looking for ways to improve Squadline." },
    { label: 'Closing', text: "Glad I could help! If you have any other questions, don't hesitate to reach out. Have a great day! 🏆" },
  ];

  useEffect(() => { loadConversations(); loadAiSettings(); }, [filter]);
  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.conversation_id);
      const interval = setInterval(() => loadMessages(selectedConversation.conversation_id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation?.conversation_id]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const url = filter === 'all' ? '/api/admin/support/all' : `/api/admin/support/all?status=${filter}`;
      const response = await fetch(`${API_URL}${url}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      setConversations(data.conversations || []);
      setCounts(data.counts || { open: 0, pending: 0, resolved: 0, total: 0 });
    } catch (error) { console.error('Error loading conversations:', error); }
    finally { setLoading(false); }
  };

  const loadAiSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/support/ai-settings`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { const data = await response.json(); setAiSettings(prev => ({ ...prev, ...data })); }
    } catch (error) { console.error('Error loading AI settings:', error); }
  };

  const checkAiHealth = async () => {
    setCheckingHealth(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/support/ai-health`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setAiHealth(await response.json());
    } catch (error) { setAiHealth({ healthy: false, error: 'Failed to reach health endpoint' }); }
    finally { setCheckingHealth(false); }
  };

  const saveAiSettings = async () => {
    setSavingSettings(true);
    try {
      await fetch(`${API_URL}/api/admin/support/ai-settings`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(aiSettings) });
      setShowSettings(false);
    } catch (error) { console.error('Error saving AI settings:', error); }
    finally { setSavingSettings(false); }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/support/conversations/${conversationId}/admin`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      setMessages(data.messages || []);
      if (data.conversation) {
        setSelectedConversation(data.conversation);
        setConversations(prev => prev.map(c => c.conversation_id === conversationId ? { ...c, ...data.conversation, unread_admin: 0 } : c));
      }
    } catch (error) { console.error('Error loading messages:', error); }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    const content = newMessage.trim();
    setSending(true); setNewMessage('');
    const optimisticMsg = { message_id: `temp_${Date.now()}`, conversation_id: selectedConversation.conversation_id, sender_type: 'admin', sender_name: 'You', content, created_at: new Date().toISOString(), read: false, is_ai: false, _optimistic: true };
    setMessages(prev => [...prev, optimisticMsg]);
    try {
      await fetch(`${API_URL}/api/admin/support/conversations/${selectedConversation.conversation_id}/messages`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ content }) });
      loadMessages(selectedConversation.conversation_id);
    } catch (error) { setMessages(prev => prev.filter(m => m.message_id !== optimisticMsg.message_id)); setNewMessage(content); }
    finally { setSending(false); inputRef.current?.focus(); }
  };

  const handleStatusChange = async (status) => {
    if (!selectedConversation) return;
    try {
      await fetch(`${API_URL}/api/admin/support/conversations/${selectedConversation.conversation_id}/status?status=${status}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
      setSelectedConversation(prev => ({ ...prev, status }));
      setConversations(prev => prev.map(c => c.conversation_id === selectedConversation.conversation_id ? { ...c, status } : c));
      loadConversations();
    } catch (error) { console.error('Error updating status:', error); }
  };

  const toggleAiForConversation = async () => {
    if (!selectedConversation) return;
    const newAiEnabled = !selectedConversation.ai_enabled;
    try {
      await fetch(`${API_URL}/api/admin/support/conversations/${selectedConversation.conversation_id}/ai-toggle?enabled=${newAiEnabled}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
      setSelectedConversation(prev => ({ ...prev, ai_enabled: newAiEnabled }));
      setConversations(prev => prev.map(c => c.conversation_id === selectedConversation.conversation_id ? { ...c, ai_enabled: newAiEnabled } : c));
    } catch (error) { console.error('Error toggling AI:', error); }
  };

  const handlePolish = async () => {
    if (!newMessage.trim()) return;
    setPolishing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/support/polish`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ text: newMessage.trim(), context: selectedConversation ? `User: ${selectedConversation.user_name}` : '' }) });
      if (response.ok) { const data = await response.json(); setNewMessage(data.polished); }
    } catch (error) { console.error('Error polishing message:', error); }
    finally { setPolishing(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const diffMs = Date.now() - new Date(dateString);
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays < 7) return `${diffDays}d`;
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFullTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatHour = (hour) => { const ampm = hour >= 12 ? 'PM' : 'AM'; return `${hour % 12 || 12}:00 ${ampm}`; };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return conv.user_name?.toLowerCase().includes(q) || conv.user_email?.toLowerCase().includes(q) || conv.last_message?.toLowerCase().includes(q);
  });

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-indigo-600" /> Support
          </h1>
          <div className="flex items-center gap-2 ml-4">
            {[
              { key: 'open', count: counts.open },
              { key: 'pending', count: counts.pending },
              { key: 'resolved', count: counts.resolved },
            ].map(({ key, count }) => {
              const cfg = STATUS_CONFIG[key];
              return (
                <button key={key} onClick={() => setFilter(key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    filter === key ? `${cfg.cls} border border-current/20` : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'
                  }`}>
                  <cfg.icon className="w-3.5 h-3.5" /> {count} {cfg.label}
                </button>
              );
            })}
            <button onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === 'all' ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'}`}>
              All
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={loadConversations}><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          <Button variant="outline" onClick={() => { setShowSettings(true); checkAiHealth(); }}>
            <Bot className="w-4 h-4 mr-2" /> AI Settings
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex rounded-2xl border border-gray-200 overflow-hidden min-h-0">
        {/* Conversation List */}
        <div className="w-96 flex flex-col bg-white border-r border-gray-200">
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search conversations..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && conversations.length === 0 ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 text-gray-400 animate-spin" /></div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Inbox className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{searchQuery ? 'No matching conversations' : `No ${filter === 'all' ? '' : filter} conversations`}</p>
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const isSelected = selectedConversation?.conversation_id === conv.conversation_id;
                const statusCfg = STATUS_CONFIG[conv.status] || STATUS_CONFIG.open;
                return (
                  <button key={conv.conversation_id} onClick={() => setSelectedConversation(conv)}
                    className={`w-full p-3 text-left border-b border-gray-100 transition-colors ${isSelected ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : 'hover:bg-gray-50 border-l-2 border-l-transparent'}`}>
                    <div className="flex items-start gap-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          {conv.user_picture ? <img src={conv.user_picture} alt="" className="w-10 h-10 rounded-full" /> : <span className="text-gray-700 font-semibold text-sm">{conv.user_name?.charAt(0)?.toUpperCase() || '?'}</span>}
                        </div>
                        {conv.unread_admin > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{conv.unread_admin > 9 ? '9+' : conv.unread_admin}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={`font-semibold text-sm truncate ${conv.unread_admin > 0 ? 'text-gray-900' : 'text-gray-700'}`}>{conv.user_name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{formatTime(conv.updated_at)}</span>
                        </div>
                        <p className={`text-xs truncate mb-1.5 ${conv.unread_admin > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>{conv.last_message || 'No messages yet'}</p>
                        <div className="flex items-center gap-1.5">
                          <Badge className={statusCfg.cls + ' text-xs'}><statusCfg.icon className="w-3 h-3 mr-1" />{statusCfg.label}</Badge>
                          {conv.ai_enabled !== false && <Badge className="bg-purple-100 text-purple-700 text-xs"><Bot className="w-3 h-3 mr-1" />AI</Badge>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                    {selectedConversation.user_picture ? <img src={selectedConversation.user_picture} alt="" className="w-9 h-9 rounded-full" /> : <span className="text-gray-700 font-semibold text-sm">{selectedConversation.user_name?.charAt(0)?.toUpperCase() || '?'}</span>}
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900 text-sm">{selectedConversation.user_name}</h2>
                    <p className="text-xs text-gray-500">{selectedConversation.user_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={toggleAiForConversation}
                    className={selectedConversation.ai_enabled !== false ? 'text-purple-700 bg-purple-50' : 'text-gray-500'}>
                    {selectedConversation.ai_enabled !== false ? <><Bot className="w-3.5 h-3.5 mr-1" />AI On</> : <><BotOff className="w-3.5 h-3.5 mr-1" />AI Off</>}
                  </Button>
                  <Select value={selectedConversation.status} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-[130px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">🔴 Open</SelectItem>
                      <SelectItem value="pending">🟡 Pending</SelectItem>
                      <SelectItem value="resolved">🟢 Resolved</SelectItem>
                      <SelectItem value="closed">⚫ Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center"><MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-500">No messages yet</p></div>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isAdmin = msg.sender_type === 'admin';
                    return (
                      <div key={msg.message_id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                        <div className="max-w-[70%]">
                          {isAdmin && msg.is_ai && <div className="flex items-center justify-end gap-1 mb-1"><Bot className="w-3 h-3 text-purple-500" /><span className="text-xs text-purple-500 font-medium">AI Response</span></div>}
                          {!isAdmin && <div className="flex items-center gap-1 mb-1"><User className="w-3 h-3 text-gray-400" /><span className="text-xs text-gray-500 font-medium">{msg.sender_name}</span></div>}
                          <div className={`px-4 py-2.5 rounded-2xl ${isAdmin ? msg.is_ai ? 'bg-purple-600 text-white rounded-br-md' : 'bg-indigo-600 text-white rounded-br-md' : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'} ${msg._optimistic ? 'opacity-70' : ''}`}>
                            <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                          </div>
                          <p className={`text-xs mt-1 ${isAdmin ? 'text-right' : 'text-left'} text-gray-400`}>{formatFullTime(msg.created_at)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Replies */}
              {showQuickReplies && (
                <div className="px-5 pb-2">
                  <Card>
                    <CardContent className="p-2">
                      <div className="flex items-center justify-between px-2 py-1 mb-1">
                        <span className="text-xs font-medium text-gray-500">Quick Replies</span>
                        <button onClick={() => setShowQuickReplies(false)}><X className="w-3.5 h-3.5 text-gray-400" /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {quickReplies.map((qr) => (
                          <button key={qr.label} onClick={() => { setNewMessage(qr.text); setShowQuickReplies(false); inputRef.current?.focus(); }}
                            className="text-left px-3 py-2 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition-colors">
                            <span className="font-medium text-gray-800">{qr.label}</span>
                            <p className="text-gray-400 truncate mt-0.5">{qr.text.slice(0, 60)}...</p>
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Input */}
              <div className="px-5 py-3 bg-white border-t border-gray-200">
                <form onSubmit={handleSendMessage} className="flex items-end gap-2">
                  <Button type="button" variant="ghost" size="icon" onClick={() => setShowQuickReplies(!showQuickReplies)} title="Quick replies"><Zap className="w-4 h-4" /></Button>
                  <div className="flex-1">
                    <textarea ref={inputRef} value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={handleKeyDown}
                      placeholder="Type your response... (Enter to send)" rows={1}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 outline-none resize-none"
                      style={{ minHeight: '42px', maxHeight: '120px' }} />
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={handlePolish} disabled={!newMessage.trim() || polishing} className="text-purple-500">
                    {polishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  </Button>
                  <Button type="submit" size="icon" disabled={!newMessage.trim() || sending}><Send className="w-4 h-4" /></Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4"><MessageCircle className="w-8 h-8 text-gray-400" /></div>
                <h3 className="text-lg font-semibold text-gray-700 mb-1">Select a conversation</h3>
                <p className="text-sm text-gray-500 max-w-xs">Choose a conversation from the list to view messages and respond to users.</p>
                {counts.open > 0 && <div className="mt-4 flex items-center justify-center gap-2 text-sm text-rose-500"><AlertCircle className="w-4 h-4" />{counts.open} conversation{counts.open !== 1 ? 's' : ''} need{counts.open === 1 ? 's' : ''} attention</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-purple-500" />AI Support Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Card>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">AI Pipeline Health</span>
                    {aiHealth && (aiHealth.healthy ? <span className="flex items-center gap-1 text-xs text-emerald-600"><Check className="w-3 h-3" />Healthy</span> : <span className="flex items-center gap-1 text-xs text-rose-500"><XCircle className="w-3 h-3" />Unhealthy</span>)}
                  </div>
                  <Button variant="outline" size="sm" onClick={checkAiHealth} disabled={checkingHealth}>{checkingHealth ? 'Checking...' : 'Run Check'}</Button>
                </div>
                {aiHealth && !aiHealth.healthy && aiHealth.error && <p className="text-xs text-rose-500 mt-2 bg-rose-50 p-2 rounded-lg">Error: {aiHealth.error}</p>}
                {aiHealth && <div className="flex gap-3 mt-2">{Object.entries(aiHealth.checks || {}).map(([key, val]) => (<span key={key} className={`text-xs flex items-center gap-1 ${val ? 'text-emerald-600' : 'text-rose-500'}`}>{val ? <Check className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}{key.replace('openai_', '').replace('_', ' ')}</span>))}</div>}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-3 flex items-center justify-between">
                <div><p className="font-medium text-gray-800 text-sm">AI Auto-Responses</p><p className="text-xs text-gray-500 mt-0.5">AI will respond to user messages automatically</p></div>
                <button onClick={() => setAiSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                  className={`relative w-11 h-6 rounded-full transition-colors ${aiSettings.enabled ? 'bg-purple-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${aiSettings.enabled ? 'left-6' : 'left-1'}`} />
                </button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-3 flex items-center justify-between">
                <div><p className="font-medium text-gray-800 text-sm">Off-Hours Only</p><p className="text-xs text-gray-500 mt-0.5">AI responds only outside business hours</p></div>
                <button onClick={() => setAiSettings(prev => ({ ...prev, offHoursOnly: !prev.offHoursOnly }))}
                  className={`relative w-11 h-6 rounded-full transition-colors ${aiSettings.offHoursOnly ? 'bg-purple-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${aiSettings.offHoursOnly ? 'left-6' : 'left-1'}`} />
                </button>
              </CardContent>
            </Card>

            {aiSettings.offHoursOnly && (
              <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                <p className="font-medium text-purple-700 text-sm mb-3 flex items-center gap-2"><Clock className="w-4 h-4" />Business Hours (AI is OFF during these hours)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-purple-600 mb-1 block">Start</label>
                    <select value={aiSettings.startHour} onChange={(e) => setAiSettings(prev => ({ ...prev, startHour: parseInt(e.target.value) }))} className="w-full px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm outline-none">
                      {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatHour(i)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-purple-600 mb-1 block">End</label>
                    <select value={aiSettings.endHour} onChange={(e) => setAiSettings(prev => ({ ...prev, endHour: parseInt(e.target.value) }))} className="w-full px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm outline-none">
                      {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatHour(i)}</option>)}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-purple-600 mt-2">AI responds between {formatHour(aiSettings.endHour)} – {formatHour(aiSettings.startHour)}</p>
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button className="flex-1 bg-purple-600 hover:bg-purple-700" onClick={saveAiSettings} disabled={savingSettings}>{savingSettings ? 'Saving...' : 'Save Settings'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
